import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/slices/authSlice";
import { clearWishlist } from "../../redux/slices/wishlistSlice";
import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
import Cookies from "js-cookie";
import axios from "axios";
import { io } from "socket.io-client";
import { globalLogout } from "../../Utils/Logout";
// Import your components
import MobileHeader from "./MobileHeader";
import DesktopHeader from "./DesktopHeader";
import Sidebar from "./Sidebar";
import DesktopNavigation from "./DesktopNavigation";

// New NotificationDropdown
const NotificationDropdown = ({
  notifications,
  unreadCount,
  onClearAll,
  onRemoveOne,
  onSeeAll,
}) => {
  return (
    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50">
      <div className="flex justify-between items-center px-3 py-2 border-b">
        <h4 className="font-semibold text-gray-700">
          Notifications {unreadCount > 0 && `(${unreadCount})`}
        </h4>
        {notifications.length > 0 && (
          <button
            onClick={onClearAll}
            className="text-sm text-red-500 hover:underline"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="max-h-60 overflow-y-auto">
        {notifications.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm">
            No notifications
          </p>
        ) : (
          notifications.map((notif, index) => (
            <div
              key={index}
              className="flex justify-between items-center px-3 py-2 hover:bg-gray-50 border-b"
            >
              <span className="text-sm text-gray-700">{notif.message || "New notification"}</span>
              <button
                onClick={() => onRemoveOne(index)}
                className="text-xs text-red-400 hover:text-red-600 ml-2"
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>

      <div className="px-3 py-2 border-t text-center">
        <button
          onClick={onSeeAll}
          className="text-blue-600 text-sm font-medium hover:underline"
        >
          See All
        </button>
      </div>
    </div>
  );
};

const Header = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [socketStatus, setSocketStatus] = useState("disconnected");
  const [isNotifDropdownOpen, setIsNotifDropdownOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const socketRef = useRef(null);

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
  const cartItems = useSelector((state) => state.cart.items);
  const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

  const staticCategories = [
    { id: "delivery", name: "Living Room Furniture", path: "/delivery", published: true },
    { id: "hotel-flight", name: "Hotel/Flight", path: "/hotel-flight", published: true },
    { id: "service", name: "Service", path: "/service", published: true },
    { id: "digital-showroom", name: "Digital Showroom", path: "/digital-showroom", published: true },
    { id: "laundry", name: "Laundry", path: "/laundry", published: true },
  ];

  // Socket.IO implementation
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      setSocketStatus("connecting");

      const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
        transports: ["websocket", "polling"],
        withCredentials: true,
        auth: { userId: user.id, userType: "Buyer" },
      });

      newSocket.on("connect", () => {
        setSocketStatus("connected");
        newSocket.emit("join", { userId: user.id, userType: "Buyer" });
      });

      newSocket.on("notification", (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      newSocket.on("disconnect", () => setSocketStatus("disconnected"));
      newSocket.on("connect_error", () => setSocketStatus("error"));

      socketRef.current = newSocket;

      return () => {
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
          setSocketStatus("disconnected");
        }
      };
    } else if (!isAuthenticated && socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocketStatus("disconnected");
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, user?.id]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
        );
        if (response.data.success) {
          const apiCategories = response.data.categories
            .filter((c) => c.published)
            .map((c) => ({ id: c.id, name: c.name, path: `/category/${c.id}`, published: c.published }));
          setCategories(apiCategories);
        }
      } catch (error) {
        console.error("Error fetching categories:", error.message);
        globalLogout(error)
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const allCategories = [...categories].filter((c) => c.published);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchWishlist());
    } else {
      dispatch(clearWishlist());
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, dispatch, location]);

  useEffect(() => {
    if (isSidebarOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => (document.body.style.overflow = "auto");
  }, [isSidebarOpen]);

  const handleLoginClick = () => {
    navigate("/login");
    closeSidebar();
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearWishlist());
    Cookies.remove("ecom_user_token");
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    navigate("/login");
    closeSidebar();
  };

  const closeSidebar = () => setIsSidebarOpen(false);

  const handleSearchSubmit = (query) => {
    navigate(`/search?query=${encodeURIComponent(query)}`);
  };

  const testSocketConnection = () => {
    if (socketRef.current) {
      socketRef.current.emit("test", { message: "Test from client", timestamp: Date.now() });
    }
  };

  // Notification handlers
  const handleClearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const handleRemoveOne = (index) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
    setUnreadCount((prev) => Math.max(prev - 1, 0));
  };

  const handleSeeAll = () => {
    navigate("/notifications");
    setIsNotifDropdownOpen(false);
  };

  return (
    <div className="bg-white shadow-md relative">
      {/* Mobile Header */}
      <MobileHeader
        isAuthenticated={isAuthenticated}
        cartCount={cartCount}
        unreadCount={unreadCount}
        notifications={notifications}
        setIsSidebarOpen={setIsSidebarOpen}
        setIsNotifDropdownOpen={setIsNotifDropdownOpen}
        onSearchSubmit={handleSearchSubmit}
        navigate={navigate}
        socketStatus={socketStatus}
        onTestSocket={testSocketConnection}
        onClearAll={handleClearAll}
        onRemoveOne={handleRemoveOne}
        onSeeAll={handleSeeAll}
      />

      {/* Desktop Header */}
      <div className="relative">
        <DesktopHeader
          isAuthenticated={isAuthenticated}
          user={user}
          wishlistCount={wishlistCount}
          cartCount={cartCount}
          unreadCount={unreadCount}
          notifications={notifications}
          onLogout={handleLogout}
          onLoginClick={handleLoginClick}
          onSearchSubmit={handleSearchSubmit}
          navigate={navigate}
          socketStatus={socketStatus}
          onTestSocket={testSocketConnection}
          setIsNotifDropdownOpen={setIsNotifDropdownOpen}
          onClearAll={handleClearAll}
          onRemoveOne={handleRemoveOne}
          onSeeAll={handleSeeAll}
        />

        {/* Notification Dropdown */}
        {isNotifDropdownOpen && (
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onClearAll={handleClearAll}
            onRemoveOne={handleRemoveOne}
            onSeeAll={handleSeeAll}
          />
        )}
      </div>

      {/* Desktop Navigation */}
      <DesktopNavigation
        categories={allCategories}
        loadingCategories={loadingCategories}
        isAuthenticated={isAuthenticated}
        location={location}
      />

      {/* Mobile Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        isAuthenticated={isAuthenticated}
        user={user}
        categories={allCategories}
        loadingCategories={loadingCategories}
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        unreadCount={unreadCount}
        location={location}
        onLoginClick={handleLoginClick}
        onLogout={handleLogout}
        navigate={navigate}
      />
    </div>
  );
};

export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";

// // Import your components
// import MobileHeader from "./MobileHeader";
// import DesktopHeader from "./DesktopHeader";
// import Sidebar from "./Sidebar";
// import DesktopNavigation from "./DesktopNavigation";

// // New NotificationDropdown
// const NotificationDropdown = ({
//   notifications,
//   unreadCount,
//   onClearAll,
//   onRemoveOne,
//   onSeeAll,
// }) => {
//   return (
//     <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50">
//       <div className="flex justify-between items-center px-3 py-2 border-b">
//         <h4 className="font-semibold text-gray-700">
//           Notifications {unreadCount > 0 && `(${unreadCount})`}
//         </h4>
//         <button
//           onClick={onClearAll}
//           className="text-sm text-red-500 hover:underline"
//         >
//           Clear All
//         </button>
//       </div>

//       <div className="max-h-60 overflow-y-auto">
//         {notifications.length === 0 ? (
//           <p className="text-gray-500 text-center py-4 text-sm">
//             No notifications
//           </p>
//         ) : (
//           notifications.map((notif, index) => (
//             <div
//               key={index}
//               className="flex justify-between items-center px-3 py-2 hover:bg-gray-50 border-b"
//             >
//               <span className="text-sm text-gray-700">{notif.message || "New notification"}</span>
//               <button
//                 onClick={() => onRemoveOne(index)}
//                 className="text-xs text-red-400 hover:text-red-600 ml-2"
//               >
//                 ✕
//               </button>
//             </div>
//           ))
//         )}
//       </div>

//       <div className="px-3 py-2 border-t text-center">
//         <button
//           onClick={onSeeAll}
//           className="text-blue-600 text-sm font-medium hover:underline"
//         >
//           See All
//         </button>
//       </div>
//     </div>
//   );
// };

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socketStatus, setSocketStatus] = useState("disconnected");
//   const [isNotifDropdownOpen, setIsNotifDropdownOpen] = useState(false);

//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const socketRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   const staticCategories = [
//     { id: "delivery", name: "Food Delivery", path: "/delivery", published: true },
//     { id: "hotel-flight", name: "Hotel/Flight", path: "/hotel-flight", published: true },
//     { id: "service", name: "Service", path: "/service", published: true },
//     { id: "digital-showroom", name: "Digital Showroom", path: "/digital-showroom", published: true },
//     { id: "laundry", name: "Laundry", path: "/laundry", published: true },
//   ];

//   // Socket.IO implementation
//   useEffect(() => {
//     if (isAuthenticated && user?.id) {
//       setSocketStatus("connecting");

//       const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//         transports: ["websocket", "polling"],
//         withCredentials: true,
//         auth: { userId: user.id, userType: "Buyer" },
//       });

//       newSocket.on("connect", () => {
//         setSocketStatus("connected");
//         newSocket.emit("join", { userId: user.id, userType: "Buyer" });
//       });

//       newSocket.on("notification", (notification) => {
//         setNotifications((prev) => [notification, ...prev]);
//         setUnreadCount((prev) => prev + 1);
//       });

//       newSocket.on("disconnect", () => setSocketStatus("disconnected"));
//       newSocket.on("connect_error", () => setSocketStatus("error"));

//       socketRef.current = newSocket;

//       return () => {
//         if (socketRef.current) {
//           socketRef.current.disconnect();
//           socketRef.current = null;
//           setSocketStatus("disconnected");
//         }
//       };
//     } else if (!isAuthenticated && socketRef.current) {
//       socketRef.current.disconnect();
//       socketRef.current = null;
//       setSocketStatus("disconnected");
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, user?.id]);

//   // Fetch categories
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
//         );
//         if (response.data.success) {
//           const apiCategories = response.data.categories
//             .filter((c) => c.published)
//             .map((c) => ({ id: c.id, name: c.name, path: `/category/${c.id}`, published: c.published }));
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error.message);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };
//     fetchCategories();
//   }, []);

//   const allCategories = [...categories, ...staticCategories].filter((c) => c.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     if (isSidebarOpen) document.body.style.overflow = "hidden";
//     else document.body.style.overflow = "auto";
//     return () => (document.body.style.overflow = "auto");
//   }, [isSidebarOpen]);

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socketRef.current) {
//       socketRef.current.disconnect();
//       socketRef.current = null;
//     }
//     navigate("/login");
//     closeSidebar();
//   };

//   const closeSidebar = () => setIsSidebarOpen(false);

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const testSocketConnection = () => {
//     if (socketRef.current) {
//       socketRef.current.emit("test", { message: "Test from client", timestamp: Date.now() });
//     }
//   };

//   // Notification handlers
//   const handleClearAll = () => {
//     setNotifications([]);
//     setUnreadCount(0);
//   };

//   const handleRemoveOne = (index) => {
//     setNotifications((prev) => prev.filter((_, i) => i !== index));
//     setUnreadCount((prev) => Math.max(prev - 1, 0));
//   };

//   const handleSeeAll = () => {
//     navigate("/notifications");
//     setIsNotifDropdownOpen(false);
//   };

//   return (
//     <div className="bg-white shadow-md relative">
//       {/* Mobile Header */}
//       <MobileHeader
//         isAuthenticated={isAuthenticated}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         notifications={notifications}
//         setIsSidebarOpen={setIsSidebarOpen}
//         setIsNotifDropdownOpen={setIsNotifDropdownOpen}
//         onSearchSubmit={handleSearchSubmit}
//         navigate={navigate}
//         socketStatus={socketStatus}
//         onTestSocket={testSocketConnection}
//       />

//       {/* Desktop Header */}
//       <div className="relative">
//         <DesktopHeader
//           isAuthenticated={isAuthenticated}
//           user={user}
//           wishlistCount={wishlistCount}
//           cartCount={cartCount}
//           unreadCount={unreadCount}
//           notifications={notifications}
//           onLogout={handleLogout}
//           onLoginClick={handleLoginClick}
//           onSearchSubmit={handleSearchSubmit}
//           navigate={navigate}
//           socketStatus={socketStatus}
//           onTestSocket={testSocketConnection}
//           setIsNotifDropdownOpen={setIsNotifDropdownOpen}
//         />

//         {/* Notification Dropdown */}
//         {isNotifDropdownOpen && (
//           <NotificationDropdown
//             notifications={notifications}
//             unreadCount={unreadCount}
//             onClearAll={handleClearAll}
//             onRemoveOne={handleRemoveOne}
//             onSeeAll={handleSeeAll}
//           />
//         )}
//       </div>

//       {/* Desktop Navigation */}
//       <DesktopNavigation
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         isAuthenticated={isAuthenticated}
//         location={location}
//       />

//       {/* Mobile Sidebar */}
//       <Sidebar
//         isOpen={isSidebarOpen}
//         onClose={closeSidebar}
//         isAuthenticated={isAuthenticated}
//         user={user}
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         wishlistCount={wishlistCount}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         location={location}
//         onLoginClick={handleLoginClick}
//         onLogout={handleLogout}
//         navigate={navigate}
//       />
//     </div>
//   );
// };

// export default Header;


// import React, { useState, useEffect, useRef } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";

// // Import your components
// import MobileHeader from "./MobileHeader";
// import DesktopHeader from "./DesktopHeader";
// import Sidebar from "./Sidebar";
// import DesktopNavigation from "./DesktopNavigation";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socketStatus, setSocketStatus] = useState("disconnected");
  
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const socketRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Socket.IO implementation
//   useEffect(() => {
//     // Only connect if authenticated and user exists
//     if (isAuthenticated && user?.id) {
//       console.log("🔌 Initializing socket connection for user:", user.id);
//       setSocketStatus("connecting");
      
//       // Create new socket connection
//       const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//         transports: ["websocket", "polling"],
//         withCredentials: true,
//         auth: {
//           userId: user.id,
//           userType: "Buyer"
//         }
//       });

//       // Set up event listeners
//       newSocket.on("connect", () => {
//         console.log("✅ Socket connected successfully:", newSocket.id);
//         setSocketStatus("connected");
        
//         // Join user's notification room
//         newSocket.emit("join", { 
//           userId: user.id, 
//           userType: "Buyer" 
//         });
//       });

//       newSocket.on("join_success", (data) => {
//         console.log("✅ Joined notification room:", data);
//       });

//       newSocket.on("notification", (notification) => {
//         console.log("📨 New notification received:", notification);
//         setNotifications(prev => [notification, ...prev]);
//         setUnreadCount(prev => prev + 1);
//       });

//       newSocket.on("disconnect", (reason) => {
//         console.log("❌ Socket disconnected:", reason);
//         setSocketStatus("disconnected");
//       });

//       newSocket.on("connect_error", (error) => {
//         console.error("❌ Socket connection error:", error.message);
//         setSocketStatus("error");
//       });

//       // Save socket to ref
//       socketRef.current = newSocket;

//       // Cleanup function
//       return () => {
//         console.log("🔌 Cleaning up socket connection");
//         if (socketRef.current) {
//           socketRef.current.disconnect();
//           socketRef.current = null;
//           setSocketStatus("disconnected");
//         }
//       };
//     } else if (!isAuthenticated && socketRef.current) {
//       // Disconnect if user logs out
//       console.log("👋 User logged out, disconnecting socket");
//       socketRef.current.disconnect();
//       socketRef.current = null;
//       setSocketStatus("disconnected");
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, user?.id]);

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         console.log("🌐 Fetching categories from API");
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         console.log("🌐 Categories API response:", response.data);
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           console.log("🌐 Processed categories:", apiCategories);
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("❌ Error fetching categories:", {
//           message: error.message,
//           response: error.response?.data,
//           status: error.response?.status
//         });
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     console.log("🔄 Auth state changed:", {
//       isAuthenticated,
//       userId: user?.id
//     });
    
//     if (isAuthenticated) {
//       console.log("❤️ Fetching wishlist for authenticated user");
//       dispatch(fetchWishlist());
//     } else {
//       console.log("❤️ User not authenticated, clearing wishlist");
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     console.log("📱 Sidebar state changed:", isSidebarOpen);
    
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const handleLoginClick = () => {
//     console.log("🔐 Login button clicked");
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     console.log("🚪 User logging out...", {
//       userId: user?.id,
//     });
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socketRef.current) {
//       console.log("🔌 Disconnecting socket on logout");
//       socketRef.current.disconnect();
//       socketRef.current = null;
//     }
//     navigate("/login");
//     closeSidebar();
//   };

//   const closeSidebar = () => {
//     console.log("📱 Closing sidebar");
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     console.log("🔍 Search submitted:", query);
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   // Function to test socket connection
//   const testSocketConnection = () => {
//     if (socketRef.current) {
//       console.log("Testing socket connection...");
//       socketRef.current.emit("test", { message: "Test from client", timestamp: Date.now() });
//     } else {
//       console.log("No active socket connection");
//     }
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Socket status indicator for debugging */}
//       <div className="hidden fixed top-0 right-0 z-50 p-2 bg-gray-800 text-white text-xs">
//         Socket: {socketStatus} | User: {user?.id || "none"}
//       </div>
      
//       {/* Mobile Header */}
//       <MobileHeader
//         isAuthenticated={isAuthenticated}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         notifications={notifications}
//         setIsSidebarOpen={setIsSidebarOpen}
//         setIsNotifDropdownOpen={(value) => {}}
//         onSearchSubmit={handleSearchSubmit}
//         navigate={navigate}
//         socketStatus={socketStatus}
//         onTestSocket={testSocketConnection}
//       />
      
//       {/* Desktop Header */}
//       <DesktopHeader
//         isAuthenticated={isAuthenticated}
//         user={user}
//         wishlistCount={wishlistCount}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         notifications={notifications}
//         onLogout={handleLogout}
//         onLoginClick={handleLoginClick}
//         onSearchSubmit={handleSearchSubmit}
//         navigate={navigate}
//         socketStatus={socketStatus}
//         onTestSocket={testSocketConnection}
//       />
      
//       {/* Desktop Navigation */}
//       <DesktopNavigation
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         isAuthenticated={isAuthenticated}
//         location={location}
//       />
      
//       {/* Mobile Sidebar */}
//       <Sidebar
//         isOpen={isSidebarOpen}
//         onClose={closeSidebar}
//         isAuthenticated={isAuthenticated}
//         user={user}
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         wishlistCount={wishlistCount}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         location={location}
//         onLoginClick={handleLoginClick}
//         onLogout={handleLogout}
//         navigate={navigate}
//       />
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";

// import MobileHeader from "./MobileHeader";
// import DesktopHeader from "./DesktopHeader";
// import Sidebar from "./Sidebar";
// import DesktopNavigation from "./DesktopNavigation";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socket, setSocket] = useState(null);
  
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // // Initialize socket connection
//   // useEffect(() => {
//   //   console.log("🔌 Socket useEffect triggered", {
//   //     isAuthenticated,
//   //     userId: user?.id,
//   //     hasSocket: !!socket
//   //   });
    
//   //   if (isAuthenticated && user) {
//   //     console.log("🔌 User is authenticated, attempting to connect to Socket.io server...");
//   //     console.log("🔌 Backend URL:", process.env.REACT_APP_BACKEND_URL);
      
//   //     const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//   //       transports: ['websocket', 'polling'],
//   //       withCredentials: true,
//   //     });
      
//   //     console.log("🔌 Socket instance created:", newSocket);
//   //     setSocket(newSocket);

//   //     newSocket.on("connect", () => {
//   //       console.log("✅ Socket connected successfully", {
//   //         socketId: newSocket.id,
//   //         connected: newSocket.connected
//   //       });
        
//   //       // Join user's notification room AFTER connection is established
//   //       const joinData = { 
//   //         userId: user.id, 
//   //         userType: "Buyer"
//   //       };
        
//   //       console.log("🔌 Emitting join event with data:", joinData);
//   //       newSocket.emit("join", joinData);
//   //     });

//   //     newSocket.on("connect_error", (error) => {
//   //       console.error("❌ Socket connection error:", {
//   //         error: error.message,
//   //         name: error.name,
//   //         stack: error.stack
//   //       });
//   //     });

//   //     newSocket.on("join_success", (data) => {
//   //       console.log("✅ Successfully joined room:", data);
//   //     });

//   //     newSocket.on("join_error", (error) => {
//   //       console.error("❌ Failed to join room:", {
//   //         error: error.message,
//   //         name: error.name
//   //       });
//   //     });

//   //     newSocket.on("disconnect", (reason) => {
//   //       console.log("❌ Socket disconnected:", {
//   //         reason,
//   //         socketId: newSocket.id
//   //       });
//   //     });

//   //     // Listen for new notifications
//   //     newSocket.on("notification", (notification) => {
//   //       console.log("📨 New notification received:", notification);
//   //       setNotifications(prev => [notification, ...prev]);
//   //       setUnreadCount(prev => prev + 1);
//   //     });

//   //     // Add more event listeners for debugging
//   //     newSocket.on("ping", (data) => {
//   //       console.log("📡 Received ping from server:", data);
//   //     });
      
//   //     newSocket.on("pong", (data) => {
//   //       console.log("📡 Received pong from server:", data);
//   //     });
      
//   //     newSocket.onAny((event, ...args) => {
//   //       console.log(`📡 Received event '${event}':`, args);
//   //     });

//   //     return () => {
//   //       console.log("🔌 Cleaning up socket connection", {
//   //         socketId: newSocket.id,
//   //         connected: newSocket.connected
//   //       });
//   //       newSocket.disconnect();
//   //     };
//   //   } else if (!isAuthenticated && socket) {
//   //     console.log("👋 User logged out, disconnecting socket", {
//   //       socketId: socket.id,
//   //       connected: socket.connected
//   //     });
//   //     socket.disconnect();
//   //     setSocket(null);
//   //     setNotifications([]);
//   //     setUnreadCount(0);
//   //   } else {
//   //     console.log("🔌 Socket connection not attempted:", {
//   //       isAuthenticated,
//   //       hasUser: !!user,
//   //       hasSocket: !!socket
//   //     });
//   //   }
//   // }, [isAuthenticated, user?.id]);

//   useEffect(() => {
//   const socket = io(process.env.REACT_APP_BACKEND_URL, {
//     transports: ["websocket", "polling"],
//     withCredentials: true,
//   });

//   socket.on("connect", () => {
//     console.log("Connected:", socket.id, user?.id);
//     socket.emit("join", { userType: "Buyer", userId: user?.id });
//   });

//   socket.on("notification", (notification) => {
//     setNotifications((prev) => [...prev, notification]);
//     console.log("📩 New notification:...............................", notification);
//   });

//   socket.on("connect_error", (err) => {
//     console.error("❌ Connection error:", err.message);
//   });

//   return () => socket.disconnect();
// }, [user?.id]);


//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         console.log("🌐 Fetching categories from API");
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         console.log("🌐 Categories API response:", response.data);
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           console.log("🌐 Processed categories:", apiCategories);
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("❌ Error fetching categories:", {
//           message: error.message,
//           response: error.response?.data,
//           status: error.response?.status
//         });
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     console.log("🔄 Auth state changed:", {
//       isAuthenticated,
//       userId: user?.id
//     });
    
//     if (isAuthenticated) {
//       console.log("❤️ Fetching wishlist for authenticated user");
//       dispatch(fetchWishlist());
//     } else {
//       console.log("❤️ User not authenticated, clearing wishlist");
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     console.log("📱 Sidebar state changed:", isSidebarOpen);
    
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const handleLoginClick = () => {
//     console.log("🔐 Login button clicked");
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     console.log("🚪 User logging out...", {
//       userId: user?.id,
//       hasSocket: !!socket
//     });
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socket) {
//       console.log("🔌 Disconnecting socket on logout", {
//         socketId: socket.id,
//         connected: socket.connected
//       });
//       socket.disconnect();
//     }
//     navigate("/login");
//     closeSidebar();
//   };

//   const closeSidebar = () => {
//     console.log("📱 Closing sidebar");
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     console.log("🔍 Search submitted:", query);
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   // Log socket connection status on mount and updates
//   useEffect(() => {
//     console.log("🔌 Socket instance updated:", {
//       hasSocket: !!socket,
//       socketId: socket?.id,
//       connected: socket?.connected
//     });
//   }, [socket]);

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Header */}
//       <MobileHeader
//         isAuthenticated={isAuthenticated}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         notifications={notifications}
//         setIsSidebarOpen={setIsSidebarOpen}
//         setIsNotifDropdownOpen={(value) => {}}
//         onSearchSubmit={handleSearchSubmit}
//         navigate={navigate}
//       />
      
//       {/* Desktop Header */}
//       <DesktopHeader
//         isAuthenticated={isAuthenticated}
//         user={user}
//         wishlistCount={wishlistCount}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         notifications={notifications}
//         onLogout={handleLogout}
//         onLoginClick={handleLoginClick}
//         onSearchSubmit={handleSearchSubmit}
//         navigate={navigate}
//       />
      
//       {/* Desktop Navigation */}
//       <DesktopNavigation
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         isAuthenticated={isAuthenticated}
//         location={location}
//       />
      
//       {/* Mobile Sidebar */}
//       <Sidebar
//         isOpen={isSidebarOpen}
//         onClose={closeSidebar}
//         isAuthenticated={isAuthenticated}
//         user={user}
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         wishlistCount={wishlistCount}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         location={location}
//         onLoginClick={handleLoginClick}
//         onLogout={handleLogout}
//         navigate={navigate}
//       />
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";

// import MobileHeader from "./MobileHeader";
// import DesktopHeader from "./DesktopHeader";
// import Sidebar from "./Sidebar";
// import DesktopNavigation from "./DesktopNavigation";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socket, setSocket] = useState(null);
  
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Initialize socket connection
//   useEffect(() => {
//     if (isAuthenticated && user) {
//       console.log("🔌 Connecting to Socket.io server...");
      
//       const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//         withCredentials: true,
//         transports: ['websocket', 'polling'],
//       });
      
//       setSocket(newSocket);

//       newSocket.on("connect", () => {
//         console.log("✅ Socket connected successfully");
        
//         // Join user's notification room AFTER connection is established
//         const joinData = { 
//           userId: user.id, 
//           userType: "Buyer"
//         };
        
//         newSocket.emit("join", joinData);
//       });

//       newSocket.on("connect_error", (error) => {
//         console.error("❌ Socket connection error:", error);
//       });

//       newSocket.on("join_success", (data) => {
//         console.log("✅ Successfully joined room:", data);
//       });

//       newSocket.on("join_error", (error) => {
//         console.error("❌ Failed to join room:", error);
//       });

//       newSocket.on("disconnect", (reason) => {
//         console.log("❌ Socket disconnected:", reason);
//       });

//       // Listen for new notifications
//       newSocket.on("notification", (notification) => {
//         console.log("📨 New notification received:", notification);
//         setNotifications(prev => [notification, ...prev]);
//         setUnreadCount(prev => prev + 1);
//       });

//       return () => {
//         console.log("🔌 Disconnecting socket...");
//         newSocket.disconnect();
//       };
//     } else if (!isAuthenticated && socket) {
//       console.log("👋 User logged out, disconnecting socket");
//       socket.disconnect();
//       setSocket(null);
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, user?.id]);

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     console.log("🚪 User logging out...");
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socket) {
//       console.log("🔌 Disconnecting socket on logout");
//       socket.disconnect();
//     }
//     navigate("/login");
//     closeSidebar();
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Header */}
//       <MobileHeader
//         isAuthenticated={isAuthenticated}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         notifications={notifications}
//         setIsSidebarOpen={setIsSidebarOpen}
//         setIsNotifDropdownOpen={(value) => {}}
//         onSearchSubmit={handleSearchSubmit}
//         navigate={navigate}
//       />
      
//       {/* Desktop Header */}
//       <DesktopHeader
//         isAuthenticated={isAuthenticated}
//         user={user}
//         wishlistCount={wishlistCount}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         notifications={notifications}
//         onLogout={handleLogout}
//         onLoginClick={handleLoginClick}
//         onSearchSubmit={handleSearchSubmit}
//         navigate={navigate}
//       />
      
//       {/* Desktop Navigation */}
//       <DesktopNavigation
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         isAuthenticated={isAuthenticated}
//         location={location}
//       />
      
//       {/* Mobile Sidebar */}
//       <Sidebar
//         isOpen={isSidebarOpen}
//         onClose={closeSidebar}
//         isAuthenticated={isAuthenticated}
//         user={user}
//         categories={allCategories}
//         loadingCategories={loadingCategories}
//         wishlistCount={wishlistCount}
//         cartCount={cartCount}
//         unreadCount={unreadCount}
//         location={location}
//         onLoginClick={handleLoginClick}
//         onLogout={handleLogout}
//         navigate={navigate}
//       />
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
//   faSignOutAlt,
//   faBell,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [isNotifDropdownOpen, setIsNotifDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socket, setSocket] = useState(null);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);
//   const notifDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Initialize socket connection
//   useEffect(() => {
//     if (isAuthenticated && user) {
//       console.log("🔌 Connecting to Socket.io server...");
//       console.log("👤 User:", user);
//       console.log("🔑 User ID:", user.id);
      
//       const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//         withCredentials: true,
//         transports: ['websocket', 'polling'],
//       });
      
//       setSocket(newSocket);

//       // Debug socket connection events
//       newSocket.on("connect", () => {
//         console.log("✅ Socket connected successfully");
//         console.log("📡 Socket ID:", newSocket.id);
        
//         // Join user's notification room AFTER connection is established
//         const joinData = { 
//           userId: user.id, 
//           userType: "Buyer"
//         };
        
//         console.log("🎯 Joining notification room with data:", joinData);
//         newSocket.emit("join", joinData);
//       });

//       newSocket.on("connect_error", (error) => {
//         console.error("❌ Socket connection error:", error);
//       });

//       newSocket.on("join_success", (data) => {
//         console.log("✅ Successfully joined room:", data);
//       });

//       newSocket.on("join_error", (error) => {
//         console.error("❌ Failed to join room:", error);
//       });

//       newSocket.on("disconnect", (reason) => {
//         console.log("❌ Socket disconnected:", reason);
//       });

//       // Listen for new notifications
//       newSocket.on("notification", (notification) => {
//         console.log("📨 New notification received:", notification);
//         setNotifications(prev => [notification, ...prev]);
//         setUnreadCount(prev => prev + 1);
//       });

//       // Debug any socket events
//       newSocket.onAny((event, ...args) => {
//         console.log("📡 Socket event:", event, args);
//       });

//       return () => {
//         console.log("🔌 Disconnecting socket...");
//         newSocket.disconnect();
//       };
//     } else if (!isAuthenticated && socket) {
//       console.log("👋 User logged out, disconnecting socket");
//       socket.disconnect();
//       setSocket(null);
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, user?.id]);

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//       if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target)) {
//         setIsNotifDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     console.log("🚪 User logging out...");
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socket) {
//       console.log("🔌 Disconnecting socket on logout");
//       socket.disconnect();
//     }
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   const toggleNotifDropdown = () => {
//     console.log("🔔 Toggling notification dropdown, current state:", isNotifDropdownOpen);
//     console.log("📊 Unread count:", unreadCount);
    
//     // Mark notifications as read when opening dropdown
//     if (!isNotifDropdownOpen && unreadCount > 0) {
//       setUnreadCount(0);
//     }
//     setIsNotifDropdownOpen(!isNotifDropdownOpen);
//   };

//   const formatNotificationTime = (dateString) => {
//     try {
//       const now = new Date();
//       const date = new Date(dateString);
//       if (isNaN(date.getTime())) return "Recently";
      
//       const diffInSeconds = Math.floor((now - date) / 1000);
      
//       if (diffInSeconds < 60) return "Just now";
//       if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
//       if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
//       if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
      
//       return date.toLocaleDateString();
//     } catch (error) {
//       return "Recently";
//     }
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {loadingCategories ? (
//               <div className="flex space-x-6">
//                 {[...Array(5)].map((_, i) => (
//                   <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//                 ))}
//               </div>
//             ) : (
//               <>
//                 {visibleCategories.map((category) => (
//                   <Link 
//                     key={category.id} 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                   </Link>
//                 ))}
                
//                 {moreCategories.length > 0 && (
//                   <div className="relative" ref={moreDropdownRef}>
//                     <button 
//                       onClick={toggleMoreDropdown}
//                       className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                         isMoreDropdownOpen ? "bg-gray-200" : ""
//                       }`}
//                     >
//                       <span>More</span>
//                       <FontAwesomeIcon 
//                         icon={faChevronDown} 
//                         className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                       />
//                     </button>
                    
//                     {isMoreDropdownOpen && (
//                       <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                         {moreCategories.map((category) => (
//                           <Link
//                             key={category.id}
//                             to={category.path}
//                             className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                             onClick={() => setIsMoreDropdownOpen(false)}
//                           >
//                             {category.name}
//                           </Link>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           {loadingCategories ? (
//             <div className="space-y-2">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <div className="space-y-2">
//               {allCategories.map((category) => (
//                 <Link
//                   key={category.id}
//                   to={category.path}
//                   className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                     location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                   }`}
//                   onClick={closeSidebar}
//                 >
//                   <span>{category.name}</span>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/notifications"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faBell} />
//                   <span>Notifications</span>
//                   {unreadCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {unreadCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <>
//                   <div className="relative" title="Cart">
//                     <Link to="/cart">
//                       <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                       {cartCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {cartCount}
//                         </span>
//                       )}
//                     </Link>
//                   </div>
                  
//                   <div className="relative" ref={notifDropdownRef} title="Notifications">
//                     <button onClick={toggleNotifDropdown}>
//                       <FontAwesomeIcon icon={faBell} size="lg" />
//                       {unreadCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {unreadCount}
//                         </span>
//                       )}
//                     </button>

//                     {isNotifDropdownOpen && (
//                       <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 border border-gray-200 max-h-96 overflow-y-auto">
//                         <div className="p-3 border-b border-gray-200">
//                           <h3 className="font-semibold text-lg">Notifications</h3>
//                         </div>
                        
//                         {notifications.length === 0 ? (
//                           <div className="p-4 text-center text-gray-500">
//                             No notifications yet
//                           </div>
//                         ) : (
//                           <div className="divide-y divide-gray-100">
//                             {notifications.map((notification) => (
//                               <Link
//                                 key={notification._id || notification.id || Math.random()}
//                                 to={notification.data?.link || "#"}
//                                 className="block px-4 py-3 hover:bg-gray-50"
//                                 onClick={() => setIsNotifDropdownOpen(false)}
//                               >
//                                 <div className="flex justify-between items-start">
//                                   <div className="flex-1">
//                                     <h4 className="font-medium text-gray-900 text-sm">
//                                       {notification.title}
//                                     </h4>
//                                     <p className="text-xs text-gray-600 mt-1">
//                                       {notification.message}
//                                     </p>
//                                   </div>
//                                   <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
//                                     {formatNotificationTime(notification.createdAt || notification.timestamp || new Date())}
//                                   </span>
//                                 </div>
//                               </Link>
//                             ))}
//                           </div>
//                         )}
                        
//                         {notifications.length > 0 && (
//                           <div className="p-3 border-t border-gray-200 text-center">
//                             <Link
//                               to="/notifications"
//                               className="text-blue-600 text-sm font-medium"
//                               onClick={() => setIsNotifDropdownOpen(false)}
//                             >
//                               View all notifications
//                             </Link>
//                           </div>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                 </>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" ref={notifDropdownRef}>
//                   <button 
//                     onClick={toggleNotifDropdown}
//                     className="relative"
//                     title="Notifications"
//                   >
//                     <FontAwesomeIcon icon={faBell} size="lg" />
//                     {unreadCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {unreadCount}
//                       </span>
//                     )}
//                   </button>

//                   {isNotifDropdownOpen && (
//                     <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 border border-gray-200 max-h-96 overflow-y-auto">
//                       <div className="p-3 border-b border-gray-200">
//                         <h3 className="font-semibold text-lg">Notifications</h3>
//                       </div>
                      
//                       {notifications.length === 0 ? (
//                         <div className="p-4 text-center text-gray-500">
//                           No notifications yet
//                         </div>
//                       ) : (
//                         <div className="divide-y divide-gray-100">
//                           {notifications.map((notification) => (
//                             <Link
//                               key={notification._id || notification.id || Math.random()}
//                               to={notification.data?.link || "#"}
//                               className="block px-4 py-3 hover:bg-gray-50"
//                               onClick={() => setIsNotifDropdownOpen(false)}
//                             >
//                               <div className="flex justify-between items-start">
//                                 <div className="flex-1">
//                                   <h4 className="font-medium text-gray-900 text-sm">
//                                     {notification.title}
//                                   </h4>
//                                   <p className="text-xs text-gray-600 mt-1">
//                                     {notification.message}
//                                   </p>
//                                 </div>
//                                 <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
//                                   {formatNotificationTime(notification.createdAt || notification.timestamp || new Date())}
//                                 </span>
//                               </div>
//                             </Link>
//                           ))}
//                         </div>
//                       )}
                      
//                       {notifications.length > 0 && (
//                         <div className="p-3 border-t border-gray-200 text-center">
//                           <Link
//                             to="/notifications"
//                             className="text-blue-600 text-sm font-medium"
//                             onClick={() => setIsNotifDropdownOpen(false)}
//                           >
//                             View all notifications
//                           </Link>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="flex items-center space-x-4">
//                 <Link
//                   to="/profile"
//                   className="text-blue-600 hover:text-blue-800"
//                   title="Profile"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" />
//                 </Link>
                
//                 <button
//                   onClick={handleLogout}
//                   className="text-red-600 hover:text-red-800"
//                   title="Logout"
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
//                 </button>
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
//   faSignOutAlt,
//   faBell,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [isNotifDropdownOpen, setIsNotifDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socket, setSocket] = useState(null);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);
//   const notifDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Initialize socket connection
//   useEffect(() => {
//     if (isAuthenticated && user) {
//       console.log("🔌 Connecting to Socket.io server...");
//       console.log("👤 User:", user);
//       console.log("🔑 User ID:", user.id);
//       console.log("👥 User Role:", user.role);
//       console.log("🏷️ User Type:", user.role.toLowerCase());
      
//       const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//         withCredentials: true,
//         transports: ['websocket',"polling"],
//       });
      
//       setSocket(newSocket);

//       // Debug socket connection events
//       newSocket.on("connect", () => {
//         console.log("✅ Socket connected successfully");
//         console.log("📡 Socket ID:", newSocket.id);
//       });

//       newSocket.on("connect_error", (error) => {
//         console.error("❌ Socket connection error:", error);
//       });

//       newSocket.on("disconnect", (reason) => {
//         console.log("❌ Socket disconnected:", reason);
//       });

//       // Join user's notification room
//       const joinData = { 
//         userId: user.id, 
//         userType: "Buyer"
//       };
      
//       console.log("🎯 Joining notification room with data:", joinData);
//       newSocket.emit("join", joinData);

//       // Listen for new notifications
//       newSocket.on("notification", (notification) => {
//         console.log("📨 New notification received:", notification);
//         setNotifications(prev => [notification, ...prev]);
//         setUnreadCount(prev => prev + 1);
//       });

//       // Debug any socket events
//       newSocket.onAny((event, ...args) => {
//         console.log("📡 Socket event:", event, args);
//       });

//       return () => {
//         console.log("🔌 Disconnecting socket...");
//         newSocket.disconnect();
//       };
//     } else if (!isAuthenticated && socket) {
//       console.log("👋 User logged out, disconnecting socket");
//       socket.disconnect();
//       setSocket(null);
//     }
//   }, [isAuthenticated, user]);

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//       if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target)) {
//         setIsNotifDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     console.log("🚪 User logging out...");
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socket) {
//       console.log("🔌 Disconnecting socket on logout");
//       socket.disconnect();
//     }
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   const toggleNotifDropdown = () => {
//     console.log("🔔 Toggling notification dropdown, current state:", isNotifDropdownOpen);
//     console.log("📊 Unread count:", unreadCount);
    
//     // Mark notifications as read when opening dropdown
//     if (!isNotifDropdownOpen && unreadCount > 0) {
//       setUnreadCount(0);
//     }
//     setIsNotifDropdownOpen(!isNotifDropdownOpen);
//   };

//   const formatNotificationTime = (dateString) => {
//     const now = new Date();
//     const date = new Date(dateString);
//     const diffInSeconds = Math.floor((now - date) / 1000);
    
//     if (diffInSeconds < 60) return "Just now";
//     if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
//     if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
//     if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
//     return date.toLocaleDateString();
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {loadingCategories ? (
//               <div className="flex space-x-6">
//                 {[...Array(5)].map((_, i) => (
//                   <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//                 ))}
//               </div>
//             ) : (
//               <>
//                 {visibleCategories.map((category) => (
//                   <Link 
//                     key={category.id} 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                   </Link>
//                 ))}
                
//                 {moreCategories.length > 0 && (
//                   <div className="relative" ref={moreDropdownRef}>
//                     <button 
//                       onClick={toggleMoreDropdown}
//                       className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                         isMoreDropdownOpen ? "bg-gray-200" : ""
//                       }`}
//                     >
//                       <span>More</span>
//                       <FontAwesomeIcon 
//                         icon={faChevronDown} 
//                         className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                       />
//                     </button>
                    
//                     {isMoreDropdownOpen && (
//                       <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                         {moreCategories.map((category) => (
//                           <Link
//                             key={category.id}
//                             to={category.path}
//                             className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                             onClick={() => setIsMoreDropdownOpen(false)}
//                           >
//                             {category.name}
//                           </Link>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           {loadingCategories ? (
//             <div className="space-y-2">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <div className="space-y-2">
//               {allCategories.map((category) => (
//                 <Link
//                   key={category.id}
//                   to={category.path}
//                   className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                     location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                   }`}
//                   onClick={closeSidebar}
//                 >
//                   <span>{category.name}</span>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/notifications"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faBell} />
//                   <span>Notifications</span>
//                   {unreadCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {unreadCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <>
//                   <div className="relative" title="Cart">
//                     <Link to="/cart">
//                       <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                       {cartCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {cartCount}
//                         </span>
//                       )}
//                     </Link>
//                   </div>
                  
//                   <div className="relative" title="Notifications">
//                     <button onClick={toggleNotifDropdown}>
//                       <FontAwesomeIcon icon={faBell} size="lg" />
//                       {unreadCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {unreadCount}
//                         </span>
//                       )}
//                     </button>
//                   </div>
//                 </>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" ref={notifDropdownRef}>
//                   <button 
//                     onClick={toggleNotifDropdown}
//                     className="relative"
//                     title="Notifications"
//                   >
//                     <FontAwesomeIcon icon={faBell} size="lg" />
//                     {unreadCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {unreadCount}
//                       </span>
//                     )}
//                   </button>

//                   {isNotifDropdownOpen && (
//                     <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 border border-gray-200 max-h-96 overflow-y-auto">
//                       <div className="p-3 border-b border-gray-200">
//                         <h3 className="font-semibold text-lg">Notifications</h3>
//                       </div>
                      
//                       {notifications.length === 0 ? (
//                         <div className="p-4 text-center text-gray-500">
//                           No notifications yet
//                         </div>
//                       ) : (
//                         <div className="divide-y divide-gray-100">
//                           {notifications.slice(0, 10).map((notification) => (
//                             <Link
//                               key={notification._id || notification.id || Math.random()}
//                               to={notification.data?.link || "#"}
//                               className="block px-4 py-3 hover:bg-gray-50"
//                               onClick={() => setIsNotifDropdownOpen(false)}
//                             >
//                               <div className="flex justify-between items-start">
//                                 <div>
//                                   <h4 className="font-medium text-gray-900">
//                                     {notification.title}
//                                   </h4>
//                                   <p className="text-sm text-gray-600 mt-1">
//                                     {notification.message}
//                                   </p>
//                                 </div>
//                                 <span className="text-xs text-gray-400">
//                                   {formatNotificationTime(notification.createdAt || new Date())}
//                                 </span>
//                               </div>
//                             </Link>
//                           ))}
//                         </div>
//                       )}
                      
//                       {notifications.length > 0 && (
//                         <div className="p-3 border-t border-gray-200 text-center">
//                           <Link
//                             to="/notifications"
//                             className="text-blue-600 text-sm font-medium"
//                             onClick={() => setIsNotifDropdownOpen(false)}
//                           >
//                             View all notifications
//                           </Link>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="flex items-center space-x-4">
//                 <Link
//                   to="/profile"
//                   className="text-blue-600 hover:text-blue-800"
//                   title="Profile"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" />
//                 </Link>
                
//                 <button
//                   onClick={handleLogout}
//                   className="text-red-600 hover:text-red-800"
//                   title="Logout"
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
//                 </button>
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
//   faSignOutAlt,
//   faBell,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [isNotifDropdownOpen, setIsNotifDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socket, setSocket] = useState(null);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);
//   const notifDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Initialize socket connection
//   useEffect(() => {
//     if (isAuthenticated && user) {
//       console.log("🔌 Connecting to Socket.io server...");
//       console.log("👤 User:", user);
//       console.log("🔑 User ID:", user.id);
//       console.log("👥 User Role:", user.role);
//       console.log("🏷️ User Type:", user.role.toLowerCase());
      
//       const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//         withCredentials: true,
//         transports: ['websocket'],
//       });
      
//       setSocket(newSocket);

//       // Debug socket connection events
//       newSocket.on("connect", () => {
//         console.log("✅ Socket connected successfully");
//         console.log("📡 Socket ID:", newSocket.id);
//       });

//       newSocket.on("connect_error", (error) => {
//         console.error("❌ Socket connection error:", error);
//       });

//       newSocket.on("disconnect", (reason) => {
//         console.log("❌ Socket disconnected:", reason);
//       });

//       // Join user's notification room
//       const joinData = { 
//         userId: user.id, 
//         userType: user.role.toLowerCase() 
//       };
      
//       console.log("🎯 Joining notification room with data:", joinData);
//       newSocket.emit("join", joinData);

//       // Listen for new notifications
//       newSocket.on("notification", (notification) => {
//         console.log("📨 New notification received:", notification);
//         setNotifications(prev => [notification, ...prev]);
//         setUnreadCount(prev => prev + 1);
//       });

//       // Debug any socket events
//       newSocket.onAny((event, ...args) => {
//         console.log("📡 Socket event:", event, args);
//       });

//       return () => {
//         console.log("🔌 Disconnecting socket...");
//         newSocket.disconnect();
//       };
//     } else if (!isAuthenticated && socket) {
//       console.log("👋 User logged out, disconnecting socket");
//       socket.disconnect();
//       setSocket(null);
//     }
//   }, [isAuthenticated, user]);

//   // Fetch existing notifications
//   useEffect(() => {
//     if (isAuthenticated) {
//       console.log("📋 Fetching existing notifications...");
//       const fetchNotifications = async () => {
//         try {
//           const response = await axios.get(
//             `${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
//             {
//               headers: {
//                 Authorization: `Bearer ${Cookies.get("ecom_user_token")}`
//               }
//             }
//           );
          
//           console.log("📋 Notifications API response:", response.data);
          
//           if (response.data.success) {
//             setNotifications(response.data.notifications);
//             setUnreadCount(response.data.unreadCount);
//             console.log(`📊 Loaded ${response.data.notifications.length} notifications, ${response.data.unreadCount} unread`);
//           }
//         } catch (error) {
//           console.error("❌ Error fetching notifications:", error);
//         }
//       };

//       fetchNotifications();
//     } else {
//       console.log("🚫 User not authenticated, clearing notifications");
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, location]);

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//       if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target)) {
//         setIsNotifDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     console.log("🚪 User logging out...");
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socket) {
//       console.log("🔌 Disconnecting socket on logout");
//       socket.disconnect();
//     }
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   const toggleNotifDropdown = async () => {
//     console.log("🔔 Toggling notification dropdown, current state:", isNotifDropdownOpen);
//     console.log("📊 Unread count:", unreadCount);
    
//     if (!isNotifDropdownOpen && unreadCount > 0) {
//       try {
//         console.log("📝 Marking notifications as read...");
//         const response = await axios.put(
//           `${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-read`,
//           {},
//           {
//             headers: {
//               Authorization: `Bearer ${Cookies.get("ecom_user_token")}`
//             }
//           }
//         );
        
//         console.log("✅ Mark as read response:", response.data);
//         setUnreadCount(0);
//       } catch (error) {
//         console.error("❌ Error marking notifications as read:", error);
//       }
//     }
//     setIsNotifDropdownOpen(!isNotifDropdownOpen);
//   };

//   const formatNotificationTime = (dateString) => {
//     const now = new Date();
//     const date = new Date(dateString);
//     const diffInSeconds = Math.floor((now - date) / 1000);
    
//     if (diffInSeconds < 60) return "Just now";
//     if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
//     if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
//     if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
//     return date.toLocaleDateString();
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {loadingCategories ? (
//               <div className="flex space-x-6">
//                 {[...Array(5)].map((_, i) => (
//                   <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//                 ))}
//               </div>
//             ) : (
//               <>
//                 {visibleCategories.map((category) => (
//                   <Link 
//                     key={category.id} 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                   </Link>
//                 ))}
                
//                 {moreCategories.length > 0 && (
//                   <div className="relative" ref={moreDropdownRef}>
//                     <button 
//                       onClick={toggleMoreDropdown}
//                       className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                         isMoreDropdownOpen ? "bg-gray-200" : ""
//                       }`}
//                     >
//                       <span>More</span>
//                       <FontAwesomeIcon 
//                         icon={faChevronDown} 
//                         className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                       />
//                     </button>
                    
//                     {isMoreDropdownOpen && (
//                       <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                         {moreCategories.map((category) => (
//                           <Link
//                             key={category.id}
//                             to={category.path}
//                             className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                             onClick={() => setIsMoreDropdownOpen(false)}
//                           >
//                             {category.name}
//                           </Link>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           {loadingCategories ? (
//             <div className="space-y-2">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <div className="space-y-2">
//               {allCategories.map((category) => (
//                 <Link
//                   key={category.id}
//                   to={category.path}
//                   className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                     location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                   }`}
//                   onClick={closeSidebar}
//                 >
//                   <span>{category.name}</span>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/notifications"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faBell} />
//                   <span>Notifications</span>
//                   {unreadCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {unreadCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <>
//                   <div className="relative" title="Cart">
//                     <Link to="/cart">
//                       <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                       {cartCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {cartCount}
//                         </span>
//                       )}
//                     </Link>
//                   </div>
                  
//                   <div className="relative" title="Notifications">
//                     <button onClick={toggleNotifDropdown}>
//                       <FontAwesomeIcon icon={faBell} size="lg" />
//                       {unreadCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {unreadCount}
//                         </span>
//                       )}
//                     </button>
//                   </div>
//                 </>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" ref={notifDropdownRef}>
//                   <button 
//                     onClick={toggleNotifDropdown}
//                     className="relative"
//                     title="Notifications"
//                   >
//                     <FontAwesomeIcon icon={faBell} size="lg" />
//                     {unreadCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {unreadCount}
//                       </span>
//                     )}
//                   </button>

//                   {isNotifDropdownOpen && (
//                     <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 border border-gray-200 max-h-96 overflow-y-auto">
//                       <div className="p-3 border-b border-gray-200">
//                         <h3 className="font-semibold text-lg">Notifications</h3>
//                       </div>
                      
//                       {notifications.length === 0 ? (
//                         <div className="p-4 text-center text-gray-500">
//                           No notifications yet
//                         </div>
//                       ) : (
//                         <div className="divide-y divide-gray-100">
//                           {notifications.slice(0, 10).map((notification) => (
//                             <Link
//                               key={notification._id}
//                               to={notification.data?.link || "#"}
//                               className="block px-4 py-3 hover:bg-gray-50"
//                               onClick={() => setIsNotifDropdownOpen(false)}
//                             >
//                               <div className="flex justify-between items-start">
//                                 <div>
//                                   <h4 className="font-medium text-gray-900">
//                                     {notification.title}
//                                   </h4>
//                                   <p className="text-sm text-gray-600 mt-1">
//                                     {notification.message}
//                                   </p>
//                                 </div>
//                                 <span className="text-xs text-gray-400">
//                                   {formatNotificationTime(notification.createdAt)}
//                                 </span>
//                               </div>
//                             </Link>
//                           ))}
//                         </div>
//                       )}
                      
//                       {notifications.length > 0 && (
//                         <div className="p-3 border-t border-gray-200 text-center">
//                           <Link
//                             to="/notifications"
//                             className="text-blue-600 text-sm font-medium"
//                             onClick={() => setIsNotifDropdownOpen(false)}
//                           >
//                             View all notifications
//                           </Link>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="flex items-center space-x-4">
//                 <Link
//                   to="/profile"
//                   className="text-blue-600 hover:text-blue-800"
//                   title="Profile"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" />
//                 </Link>
                
//                 <button
//                   onClick={handleLogout}
//                   className="text-red-600 hover:text-red-800"
//                   title="Logout"
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
//                 </button>
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
//   faSignOutAlt,
//   faBell,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import { io } from "socket.io-client";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [isNotifDropdownOpen, setIsNotifDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const [notifications, setNotifications] = useState([]);
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [socket, setSocket] = useState(null);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);
//   const notifDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Initialize socket connection
//   useEffect(() => {
//     if (isAuthenticated && user) {
//       const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
//         withCredentials: true,
//         extraHeaders: {
//           "Authorization": `Bearer ${Cookies.get("ecom_user_token")}`
//         }
//       });

      
      
//       setSocket(newSocket);

//       // Join user's notification room
//       newSocket.emit("join", { 
//         userId: user.id, 
//         userType: user.role.toLowerCase() 
//       });

//       // Listen for new notifications
//       newSocket.on("notification", (notification) => {
//         setNotifications(prev => [notification, ...prev]);
//         setUnreadCount(prev => prev + 1);
//       });

//       return () => {
//         newSocket.disconnect();
//       };
//     }
//   }, [isAuthenticated, user]);

//   // Fetch existing notifications
//   useEffect(() => {
//     if (isAuthenticated) {
//       const fetchNotifications = async () => {
//         try {
//           const response = await axios.get(
//             `${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
//             {
//               headers: {
//                 Authorization: `Bearer ${Cookies.get("ecom_user_token")}`
//               }
//             }
//           );
          
//           if (response.data.success) {
//             setNotifications(response.data.notifications);
//             setUnreadCount(response.data.unreadCount);
//           }
//         } catch (error) {
//           console.error("Error fetching notifications:", error);
//         }
//       };

//       fetchNotifications();
//     }
//   }, [isAuthenticated, location]);

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//       setNotifications([]);
//       setUnreadCount(0);
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//       if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target)) {
//         setIsNotifDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     if (socket) socket.disconnect();
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   const toggleNotifDropdown = async () => {
//     if (!isNotifDropdownOpen && unreadCount > 0) {
//       try {
//         // Mark notifications as read when dropdown is opened
//         await axios.put(
//           `${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-read`,
//           {},
//           {
//             headers: {
//               Authorization: `Bearer ${Cookies.get("ecom_user_token")}`
//             }
//           }
//         );
//         setUnreadCount(0);
//       } catch (error) {
//         console.error("Error marking notifications as read:", error);
//       }
//     }
//     setIsNotifDropdownOpen(!isNotifDropdownOpen);
//   };

//   const formatNotificationTime = (dateString) => {
//     const now = new Date();
//     const date = new Date(dateString);
//     const diffInSeconds = Math.floor((now - date) / 1000);
    
//     if (diffInSeconds < 60) return "Just now";
//     if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
//     if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
//     if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
//     return date.toLocaleDateString();
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {loadingCategories ? (
//               <div className="flex space-x-6">
//                 {[...Array(5)].map((_, i) => (
//                   <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//                 ))}
//               </div>
//             ) : (
//               <>
//                 {visibleCategories.map((category) => (
//                   <Link 
//                     key={category.id} 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                   </Link>
//                 ))}
                
//                 {moreCategories.length > 0 && (
//                   <div className="relative" ref={moreDropdownRef}>
//                     <button 
//                       onClick={toggleMoreDropdown}
//                       className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                         isMoreDropdownOpen ? "bg-gray-200" : ""
//                       }`}
//                     >
//                       <span>More</span>
//                       <FontAwesomeIcon 
//                         icon={faChevronDown} 
//                         className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                       />
//                     </button>
                    
//                     {isMoreDropdownOpen && (
//                       <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                         {moreCategories.map((category) => (
//                           <Link
//                             key={category.id}
//                             to={category.path}
//                             className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                             onClick={() => setIsMoreDropdownOpen(false)}
//                           >
//                             {category.name}
//                           </Link>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           {loadingCategories ? (
//             <div className="space-y-2">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <div className="space-y-2">
//               {allCategories.map((category) => (
//                 <Link
//                   key={category.id}
//                   to={category.path}
//                   className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                     location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                   }`}
//                   onClick={closeSidebar}
//                 >
//                   <span>{category.name}</span>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/notifications"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faBell} />
//                   <span>Notifications</span>
//                   {unreadCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {unreadCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <>
//                   <div className="relative" title="Cart">
//                     <Link to="/cart">
//                       <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                       {cartCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {cartCount}
//                         </span>
//                       )}
//                     </Link>
//                   </div>
                  
//                   <div className="relative" title="Notifications">
//                     <button onClick={toggleNotifDropdown}>
//                       <FontAwesomeIcon icon={faBell} size="lg" />
//                       {unreadCount > 0 && (
//                         <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                           {unreadCount}
//                         </span>
//                       )}
//                     </button>
//                   </div>
//                 </>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" ref={notifDropdownRef}>
//                   <button 
//                     onClick={toggleNotifDropdown}
//                     className="relative"
//                     title="Notifications"
//                   >
//                     <FontAwesomeIcon icon={faBell} size="lg" />
//                     {unreadCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {unreadCount}
//                       </span>
//                     )}
//                   </button>

//                   {isNotifDropdownOpen && (
//                     <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 border border-gray-200 max-h-96 overflow-y-auto">
//                       <div className="p-3 border-b border-gray-200">
//                         <h3 className="font-semibold text-lg">Notifications</h3>
//                       </div>
                      
//                       {notifications.length === 0 ? (
//                         <div className="p-4 text-center text-gray-500">
//                           No notifications yet
//                         </div>
//                       ) : (
//                         <div className="divide-y divide-gray-100">
//                           {notifications.slice(0, 10).map((notification) => (
//                             <Link
//                               key={notification._id}
//                               to={notification.data?.link || "#"}
//                               className="block px-4 py-3 hover:bg-gray-50"
//                               onClick={() => setIsNotifDropdownOpen(false)}
//                             >
//                               <div className="flex justify-between items-start">
//                                 <div>
//                                   <h4 className="font-medium text-gray-900">
//                                     {notification.title}
//                                   </h4>
//                                   <p className="text-sm text-gray-600 mt-1">
//                                     {notification.message}
//                                   </p>
//                                 </div>
//                                 <span className="text-xs text-gray-400">
//                                   {formatNotificationTime(notification.createdAt)}
//                                 </span>
//                               </div>
//                             </Link>
//                           ))}
//                         </div>
//                       )}
                      
//                       {notifications.length > 0 && (
//                         <div className="p-3 border-t border-gray-200 text-center">
//                           <Link
//                             to="/notifications"
//                             className="text-blue-600 text-sm font-medium"
//                             onClick={() => setIsNotifDropdownOpen(false)}
//                           >
//                             View all notifications
//                           </Link>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="flex items-center space-x-4">
//                 <Link
//                   to="/profile"
//                   className="text-blue-600 hover:text-blue-800"
//                   title="Profile"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" />
//                 </Link>
                
//                 <button
//                   onClick={handleLogout}
//                   className="text-red-600 hover:text-red-800"
//                   title="Logout"
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
//                 </button>
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
//   faSignOutAlt,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {loadingCategories ? (
//               <div className="flex space-x-6">
//                 {[...Array(5)].map((_, i) => (
//                   <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//                 ))}
//               </div>
//             ) : (
//               <>
//                 {visibleCategories.map((category) => (
//                   <Link 
//                     key={category.id} 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                   </Link>
//                 ))}
                
//                 {moreCategories.length > 0 && (
//                   <div className="relative" ref={moreDropdownRef}>
//                     <button 
//                       onClick={toggleMoreDropdown}
//                       className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                         isMoreDropdownOpen ? "bg-gray-200" : ""
//                       }`}
//                     >
//                       <span>More</span>
//                       <FontAwesomeIcon 
//                         icon={faChevronDown} 
//                         className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                       />
//                     </button>
                    
//                     {isMoreDropdownOpen && (
//                       <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                         {moreCategories.map((category) => (
//                           <Link
//                             key={category.id}
//                             to={category.path}
//                             className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                             onClick={() => setIsMoreDropdownOpen(false)}
//                           >
//                             {category.name}
//                           </Link>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           {loadingCategories ? (
//             <div className="space-y-2">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <div className="space-y-2">
//               {allCategories.map((category) => (
//                 <Link
//                   key={category.id}
//                   to={category.path}
//                   className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                     location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                   }`}
//                   onClick={closeSidebar}
//                 >
//                   <span>{category.name}</span>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="flex items-center space-x-4">
//                 <Link
//                   to="/profile"
//                   className="text-blue-600 hover:text-blue-800"
//                   title="Profile"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" />
//                 </Link>
                
//                 <button
//                   onClick={handleLogout}
//                   className="text-red-600 hover:text-red-800"
//                   title="Logout"
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
//                 </button>
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
//   faSignOutAlt,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {loadingCategories ? (
//               <div className="flex space-x-6">
//                 {[...Array(5)].map((_, i) => (
//                   <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//                 ))}
//               </div>
//             ) : (
//               <>
//                 {visibleCategories.map((category) => (
//                   <Link 
//                     key={category.id} 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                   </Link>
//                 ))}
                
//                 {moreCategories.length > 0 && (
//                   <div className="relative" ref={moreDropdownRef}>
//                     <button 
//                       onClick={toggleMoreDropdown}
//                       className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                         isMoreDropdownOpen ? "bg-gray-200" : ""
//                       }`}
//                     >
//                       <span>More</span>
//                       <FontAwesomeIcon 
//                         icon={faChevronDown} 
//                         className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                       />
//                     </button>
                    
//                     {isMoreDropdownOpen && (
//                       <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                         {moreCategories.map((category) => (
//                           <Link
//                             key={category.id}
//                             to={category.path}
//                             className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                             onClick={() => setIsMoreDropdownOpen(false)}
//                           >
//                             {category.name}
//                           </Link>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           {loadingCategories ? (
//             <div className="space-y-2">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <div className="space-y-2">
//               {allCategories.map((category) => (
//                 <Link
//                   key={category.id}
//                   to={category.path}
//                   className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                     location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                   }`}
//                   onClick={closeSidebar}
//                 >
//                   <span>{category.name}</span>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <IoExitOutline className="text-xl" />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <>
//                 <div className="relative" ref={dropdownRef}>
//                   <button 
//                     onClick={toggleUserDropdown}
//                     className="flex items-center space-x-2 focus:outline-none"
//                   >
//                     <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                     <span className="text-blue-600 font-medium hidden md:inline">
//                       {user?.name.split(' ')[0] || "Profile"}
//                     </span>
//                     <FontAwesomeIcon 
//                       icon={faChevronDown} 
//                       className={`text-xs transition-transform ${isUserDropdownOpen ? 'transform rotate-180' : ''}`}
//                     />
//                   </button>
                  
//                   {isUserDropdownOpen && (
//                     <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                       <Link
//                         to="/profile"
//                         className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                         onClick={() => setIsUserDropdownOpen(false)}
//                       >
//                         Profile
//                       </Link>
//                     </div>
//                   )}
//                 </div>
                
//                 {/* Direct Logout Button */}
//                 <button
//                   onClick={handleLogout}
//                   className="text-gray-600 hover:text-red-600 transition-colors"
//                   title="Logout"
//                 >
//                   <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
//                 </button>
//               </>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loadingCategories, setLoadingCategories] = useState(true);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery",
//       published: true
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight",
//       published: true
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service",
//       published: true
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom",
//       published: true
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry",
//       published: true
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           // Filter categories to only include published ones
//           const apiCategories = response.data.categories
//             .filter(category => category.published)
//             .map(category => ({
//               id: category.id,
//               name: category.name,
//               path: `/category/${category.id}`,
//               published: category.published
//             }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoadingCategories(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Combine API categories with static categories and filter published ones
//   const allCategories = [
//     ...categories,
//     ...staticCategories
//   ].filter(category => category.published);

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {loadingCategories ? (
//               <div className="flex space-x-6">
//                 {[...Array(5)].map((_, i) => (
//                   <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//                 ))}
//               </div>
//             ) : (
//               <>
//                 {visibleCategories.map((category) => (
//                   <Link 
//                     key={category.id} 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                   </Link>
//                 ))}
                
//                 {moreCategories.length > 0 && (
//                   <div className="relative" ref={moreDropdownRef}>
//                     <button 
//                       onClick={toggleMoreDropdown}
//                       className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                         isMoreDropdownOpen ? "bg-gray-200" : ""
//                       }`}
//                     >
//                       <span>More</span>
//                       <FontAwesomeIcon 
//                         icon={faChevronDown} 
//                         className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                       />
//                     </button>
                    
//                     {isMoreDropdownOpen && (
//                       <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                         {moreCategories.map((category) => (
//                           <Link
//                             key={category.id}
//                             to={category.path}
//                             className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                             onClick={() => setIsMoreDropdownOpen(false)}
//                           >
//                             {category.name}
//                           </Link>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           {loadingCategories ? (
//             <div className="space-y-2">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-10 w-full bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <div className="space-y-2">
//               {allCategories.map((category) => (
//                 <Link
//                   key={category.id}
//                   to={category.path}
//                   className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                     location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                   }`}
//                   onClick={closeSidebar}
//                 >
//                   <span>{category.name}</span>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <IoExitOutline className="text-xl" />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="relative" ref={dropdownRef}>
//                 <button 
//                   onClick={toggleUserDropdown}
//                   className="flex items-center space-x-2 focus:outline-none"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                   <span className="text-blue-600 font-medium hidden md:inline">
//                     {user?.name.split(' ')[0] || "Profile"}
//                   </span>
//                   <FontAwesomeIcon 
//                     icon={faChevronDown} 
//                     className={`text-xs transition-transform ${isUserDropdownOpen ? 'transform rotate-180' : ''}`}
//                   />
//                 </button>
                
//                 {isUserDropdownOpen && (
//                   <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                     <Link
//                       to="/profile"
//                       className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                       onClick={() => setIsUserDropdownOpen(false)}
//                     >
//                       Profile
//                     </Link>
//                     <button
//                       onClick={handleLogout}
//                       className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
//                     >
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faEllipsisH,
// } from "@fortawesome/free-solid-svg-icons";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);
//   const moreDropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       path: "/delivery"
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       path: "/hotel-flight"
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       path: "/service"
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       path: "/digital-showroom"
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       path: "/laundry"
//     }
//   ];

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           const apiCategories = response.data.categories.map(category => ({
//             id: category.id,
//             name: category.name,
//             path: `/category/${category.id}`
//           }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       }
//     };

//     fetchCategories();
//   }, []);

//   const allCategories = [...categories, ...staticCategories];

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   // Desktop Navigation - Only show "Become a Seller" when authenticated
//   const DesktopNavigation = () => {
//     // Determine which categories to show and which to put in "More" dropdown
//     const visibleCategories = allCategories.slice(0, MAX_VISIBLE_CATEGORIES);
//     const moreCategories = allCategories.length > MAX_VISIBLE_CATEGORIES 
//       ? allCategories.slice(MAX_VISIBLE_CATEGORIES)
//       : [];

//     return (
//       <div className="bg-gray-100 border-t">
//         <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//           <div className="flex flex-wrap gap-2 md:gap-6">
//             {visibleCategories.map((category) => (
//               <Link 
//                 key={category.id} 
//                 to={category.path}
//                 className={getLinkClass(category.path)}
//               >
//                 <span className="ml-2">{category.name}</span>
//               </Link>
//             ))}
            
//             {moreCategories.length > 0 && (
//               <div className="relative" ref={moreDropdownRef}>
//                 <button 
//                   onClick={toggleMoreDropdown}
//                   className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                     isMoreDropdownOpen ? "bg-gray-200" : ""
//                   }`}
//                 >
//                   <span>More</span>
//                   <FontAwesomeIcon 
//                     icon={faChevronDown} 
//                     className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                   />
//                 </button>
                
//                 {isMoreDropdownOpen && (
//                   <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                     {moreCategories.map((category) => (
//                       <Link
//                         key={category.id}
//                         to={category.path}
//                         className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                         onClick={() => setIsMoreDropdownOpen(false)}
//                       >
//                         {category.name}
//                       </Link>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}
//           </div>
          
//           {isAuthenticated && (
//             <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//               <FontAwesomeIcon icon={faPlus} />
//               <span className="ml-2">Become a Seller</span>
//             </Link>
//           )}
//         </div>
//       </div>
//     );
//   };

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           <div className="space-y-2">
//             {allCategories.map((category) => (
//               <Link
//                 key={category.id}
//                 to={category.path}
//                 className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                   location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                 }`}
//                 onClick={closeSidebar}
//               >
//                 <span>{category.name}</span>
//               </Link>
//             ))}
//           </div>
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <IoExitOutline className="text-xl" />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="relative" ref={dropdownRef}>
//                 <button 
//                   onClick={toggleUserDropdown}
//                   className="flex items-center space-x-2 focus:outline-none"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                   <span className="text-blue-600 font-medium hidden md:inline">
//                     {user?.name.split(' ')[0] || "Profile"}
//                   </span>
//                   <FontAwesomeIcon 
//                     icon={faChevronDown} 
//                     className={`text-xs transition-transform ${isUserDropdownOpen ? 'transform rotate-180' : ''}`}
//                   />
//                 </button>
                
//                 {isUserDropdownOpen && (
//                   <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                     <Link
//                       to="/profile"
//                       className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                       onClick={() => setIsUserDropdownOpen(false)}
//                     >
//                       Profile
//                     </Link>
//                     <button
//                       onClick={handleLogout}
//                       className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
//                     >
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faUtensils,
//   faBed,
//   faTools,
//   faMobileScreen,
//   faBasketShopping,
//   faLaptop,
//   faShirt,
//   faPills,
// } from "@fortawesome/free-solid-svg-icons";
// import { MdLocalLaundryService } from "react-icons/md";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       icon: faUtensils,
//       path: "/delivery"
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       icon: faBed,
//       path: "/hotel-flight"
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       icon: faTools,
//       path: "/service"
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       icon: faMobileScreen,
//       path: "/digital-showroom"
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       icon: MdLocalLaundryService,
//       path: "/laundry"
//     }
//   ];

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`,
//         );
        
//         if (response.data.success) {
//           const apiCategories = response.data.categories.map(category => ({
//             id: category.id,
//             name: category.name,
//             icon: getCategoryIcon(category.name),
//             path: `/category/${category.id}`
//           }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       }
//     };

//     fetchCategories();
//   }, []);

//   const getCategoryIcon = (categoryName) => {
//     switch(categoryName.toLowerCase()) {
//       case 'grocery':
//         return faBasketShopping;
//       case 'electronics':
//         return faLaptop;
//       case 'clothing':
//         return faShirt;
//       case 'medicine':
//         return faPills;
//       default:
//         return null;
//     }
//   };

//   const allCategories = [...categories, ...staticCategories];

//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   // Desktop Navigation - Only show "Become a Seller" when authenticated
//   const DesktopNavigation = () => (
//     <div className="bg-gray-100 border-t">
//       <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//         <div className="flex flex-wrap gap-2 md:gap-6">
//           {allCategories.map((category) => (
//             <Link 
//               key={category.id} 
//               to={category.path}
//               className={getLinkClass(category.path)}
//             >
//               {typeof category.icon === 'function' ? (
//                 <category.icon className="text-lg" />
//               ) : (
//                 <FontAwesomeIcon icon={category.icon} />
//               )}
//               <span className="ml-2">{category.name}</span>
//             </Link>
//           ))}
//         </div>
        
//         {isAuthenticated && (
//           <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//             <FontAwesomeIcon icon={faPlus} />
//             <span className="ml-2">Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     </div>
//   );

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           <div className="space-y-2">
//             {allCategories.map((category) => (
//               <Link
//                 key={category.id}
//                 to={category.path}
//                 className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                   location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                 }`}
//                 onClick={closeSidebar}
//               >
//                 {typeof category.icon === 'function' ? (
//                   <category.icon className="text-xl" />
//                 ) : (
//                   <FontAwesomeIcon icon={category.icon} />
//                 )}
//                 <span>{category.name}</span>
//               </Link>
//             ))}
//           </div>
//         </div>

//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             {isAuthenticated && (
//               <>
//                 <Link
//                   to="/wishlist"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faHeart} />
//                   <span>Wishlist</span>
//                   {wishlistCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {wishlistCount}
//                     </span>
//                   )}
//                 </Link>
//                 <Link
//                   to="/cart"
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//                   onClick={closeSidebar}
//                 >
//                   <FontAwesomeIcon icon={faShoppingCart} />
//                   <span>Cart</span>
//                   {cartCount > 0 && (
//                     <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//                 <button
//                   className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                   onClick={handleLogout}
//                 >
//                   <IoExitOutline className="text-xl" />
//                   <span>Logout</span>
//                 </button>
//               </>
//             )}
//           </div>
//         </div>

//         {isAuthenticated && (
//           <Link
//             to="/become-seller"
//             className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//             onClick={closeSidebar}
//           >
//             <FontAwesomeIcon icon={faPlus} />
//             <span>Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               {isAuthenticated && (
//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               )}

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             {isAuthenticated && (
//               <>
//                 <div className="relative" title="Wishlist">
//                   <Link to="/wishlist" className="flex items-center space-x-2">
//                     <FontAwesomeIcon icon={faHeart} size="lg" />
//                     {wishlistCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {wishlistCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>

//                 <div className="relative" title="Cart">
//                   <Link to="/cart">
//                     <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                     {cartCount > 0 && (
//                       <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                         {cartCount}
//                       </span>
//                     )}
//                   </Link>
//                 </div>
//               </>
//             )}

//             {isAuthenticated ? (
//               <div className="relative" ref={dropdownRef}>
//                 <button 
//                   onClick={toggleUserDropdown}
//                   className="flex items-center space-x-2 focus:outline-none"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                   <span className="text-blue-600 font-medium hidden md:inline">
//                     {user?.name.split(' ')[0] || "Profile"}
//                   </span>
//                   <FontAwesomeIcon 
//                     icon={faChevronDown} 
//                     className={`text-xs transition-transform ${isUserDropdownOpen ? 'transform rotate-180' : ''}`}
//                   />
//                 </button>
                
//                 {isUserDropdownOpen && (
//                   <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                     <Link
//                       to="/profile"
//                       className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                       onClick={() => setIsUserDropdownOpen(false)}
//                     >
//                       Profile
//                     </Link>
//                     <button
//                       onClick={handleLogout}
//                       className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
//                     >
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faPlus,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faUtensils,
//   faBed,
//   faTools,
//   faMobileScreen,
//   faBasketShopping,
//   faLaptop,
//   faShirt,
//   faPills,
// } from "@fortawesome/free-solid-svg-icons";
// import { MdLocalLaundryService } from "react-icons/md";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import axios from "axios";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       icon: faUtensils,
//       path: "/category/delivery"
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       icon: faBed,
//       path: "/category/hotel-flight"
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       icon: faTools,
//       path: "/category/service"
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       icon: faMobileScreen,
//       path: "/category/digital-showroom"
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       icon: MdLocalLaundryService,
//       path: "/category/laundry"
//     }
//   ];

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         // const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/seller/category/list",
//           // {
//           //   headers: {
//           //     Authorization: `Bearer ${token}`,
//           //   },
//           // }
//         );
        
//         if (response.data.success) {
//           // Transform API categories to match our structure
//           const apiCategories = response.data.categories.map(category => ({
//             id: category.id,
//             name: category.name,
//             icon: getCategoryIcon(category.name),
//             path: `/category/${category.id}`
//           }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Helper function to get appropriate icon for each category
//   const getCategoryIcon = (categoryName) => {
//     switch(categoryName.toLowerCase()) {
//       case 'grocery':
//         return faBasketShopping;
//       case 'electronics':
//         return faLaptop;
//       case 'clothing':
//         return faShirt;
//       case 'medicine':
//         return faPills;
//       default:
//         return null;
//     }
//   };

//   // Combine API categories with static categories
//   const allCategories = [...categories, ...staticCategories];

//   // Fetch wishlist when authenticated
//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Prevent body scroll when sidebar is open
//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => (
//     <div className="bg-gray-100 border-t">
//       <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//         <div className="flex flex-wrap gap-2 md:gap-6">
//           {allCategories.map((category) => (
//             <Link 
//               key={category.id} 
//               to={category.path}
//               className={getLinkClass(category.path)}
//             >
//               {typeof category.icon === 'function' ? (
//                 <category.icon className="text-lg" />
//               ) : (
//                 <FontAwesomeIcon icon={category.icon} />
//               )}
//               <span className="ml-2">{category.name}</span>
//             </Link>
//           ))}
//         </div>
        
//         <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//           <FontAwesomeIcon icon={faPlus} />
//           <span className="ml-2">Become a Seller</span>
//         </Link>
//       </div>
//     </div>
//   );

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         {/* Close Button */}
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         {/* User Section */}
//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         {/* Navigation Links */}
//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           <div className="space-y-2">
//             {allCategories.map((category) => (
//               <Link
//                 key={category.id}
//                 to={category.path}
//                 className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                   location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                 }`}
//                 onClick={closeSidebar}
//               >
//                 {typeof category.icon === 'function' ? (
//                   <category.icon className="text-xl" />
//                 ) : (
//                   <FontAwesomeIcon icon={category.icon} />
//                 )}
//                 <span>{category.name}</span>
//               </Link>
//             ))}
//           </div>
//         </div>

//         {/* User Actions */}
//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             <Link
//               to="/wishlist"
//               className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//               onClick={closeSidebar}
//             >
//               <FontAwesomeIcon icon={faHeart} />
//               <span>Wishlist</span>
//               {wishlistCount > 0 && (
//                 <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                   {wishlistCount}
//                 </span>
//               )}
//             </Link> 
//             {isAuthenticated && (
//               <button
//                 className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                 onClick={handleLogout}
//               >
//                 <IoExitOutline className="text-xl" />
//                 <span>Logout</span>
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Become a Seller */}
//         <Link
//           to="/become-seller"
//           className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//           onClick={closeSidebar}
//         >
//           <FontAwesomeIcon icon={faPlus} />
//           <span>Become a Seller</span>
//         </Link>
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               <div className="relative" title="Cart">
//                 <Link to="/cart">
//                   <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                   {cartCount > 0 && (
//                     <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//               </div>

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             <div className="relative" title="Wishlist">
//               <Link to="/wishlist" className="flex items-center space-x-2">
//                 <FontAwesomeIcon icon={faHeart} size="lg" />
//                 {wishlistCount > 0 && (
//                   <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                     {wishlistCount}
//                   </span>
//                 )}
//               </Link>
//             </div>

//             <div className="relative" title="Cart">
//               <Link to="/cart">
//                 <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                 {cartCount > 0 && (
//                   <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                     {cartCount}
//                   </span>
//                 )}
//               </Link>
//             </div>
//             {isAuthenticated ? (
//               <div className="relative" ref={dropdownRef}>
//                 <button 
//                   onClick={toggleUserDropdown}
//                   className="flex items-center space-x-2 focus:outline-none"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                   <span className="text-blue-600 font-medium hidden md:inline">
//                     {user?.name.split(' ')[0] || "Profile"}
//                   </span>
//                   <FontAwesomeIcon 
//                     icon={faChevronDown} 
//                     className={`text-xs transition-transform ${isUserDropdownOpen ? 'transform rotate-180' : ''}`}
//                   />
//                 </button>
                
//                 {isUserDropdownOpen && (
//                   <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                     <Link
//                       to="/profile"
//                       className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                       onClick={() => setIsUserDropdownOpen(false)}
//                     >
//                       Profile
//                     </Link>
//                     <button
//                       onClick={handleLogout}
//                       className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
//                     >
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faHome,
//   faPlus,
//   faShoppingBasket,
//   faSuitcase,
//   faTruck,
//   faStore,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faPills,
//   faUtensils,
//   faBed,
//   faTools,
//   faMobileScreen,
//   faShirt,
//   faLaptop,
//   faBasketShopping,
// } from "@fortawesome/free-solid-svg-icons";
// import { MdLocalLaundryService } from "react-icons/md";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import SearchBar from "./Searchbar";
// import axios from "axios";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Static categories that should always be shown
//   const staticCategories = [
//     { 
//       id: "delivery", 
//       name: "Food Delivery", 
//       identifier: "#delivery", 
//       icon: faUtensils,
//       path: "/delivery"
//     },
//     { 
//       id: "hotel-flight", 
//       name: "Hotel/Flight", 
//       identifier: "#hotel-flight", 
//       icon: faBed,
//       path: "/hotel-flight"
//     },
//     { 
//       id: "service", 
//       name: "Service", 
//       identifier: "#service", 
//       icon: faTools,
//       path: "/service"
//     },
//     { 
//       id: "digital-showroom", 
//       name: "Digital Showroom", 
//       identifier: "#digital-showroom", 
//       icon: faMobileScreen,
//       path: "/digital-showroom"
//     },
//     { 
//       id: "laundry", 
//       name: "Laundry", 
//       identifier: "#laundry", 
//       icon: MdLocalLaundryService,
//       path: "/laundry"
//     }
//   ];

//   // Fetch categories from API
//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           "https://seller-backend-29o9.onrender.com/api/seller/category/list",
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
        
//         if (response.data.success) {
//           // Transform API categories to match our structure
//           const apiCategories = response.data.categories.map(category => ({
//             id: category.id,
//             name: category.name,
//             identifier: category.identifier,
//             icon: getCategoryIcon(category.name),
//             path: `/${category.name.toLowerCase().replace(/\s+/g, '-')}`
//           }));
          
//           setCategories(apiCategories);
//         }
//       } catch (error) {
//         console.error("Error fetching categories:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   // Helper function to get appropriate icon for each category
//   const getCategoryIcon = (categoryName) => {
//     switch(categoryName.toLowerCase()) {
//       case 'grocery':
//         return faBasketShopping;
//       case 'electronics':
//         return faLaptop;
//       case 'clothing':
//         return faShirt;
//       case 'medicine':
//         return faPills;
//       default:
//         return faStore;
//     }
//   };

//   // Combine API categories with static categories
//   const allCategories = [...categories, ...staticCategories];

//   // Fetch wishlist when authenticated
//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Prevent body scroll when sidebar is open
//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => (
//     <div className="bg-gray-100 border-t">
//       <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//         <div className="flex flex-wrap gap-2 md:gap-6">
//           {allCategories.map((category) => (
//             <Link 
//               key={category.id} 
//               to={category.path} 
//               className={getLinkClass(category.path)}
//             >
//               {typeof category.icon === 'function' ? (
//                 <category.icon className="text-lg" />
//               ) : (
//                 <FontAwesomeIcon icon={category.icon} />
//               )}
//               <span className="ml-2">{category.name}</span>
//             </Link>
//           ))}
//         </div>
        
//         <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//           <FontAwesomeIcon icon={faPlus} />
//           <span className="ml-2">Become a Seller</span>
//         </Link>
//       </div>
//     </div>
//   );

//   const renderSidebarContent = () => {
//     return (
//       <div className="p-6">
//         {/* Close Button */}
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         {/* User Section */}
//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         {/* Navigation Links */}
//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           <div className="space-y-2">
//             {allCategories.map((category) => (
//               <Link
//                 key={category.id}
//                 to={category.path}
//                 className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                   location.pathname === category.path ? "bg-blue-50 text-blue-600" : ""
//                 }`}
//                 onClick={closeSidebar}
//               >
//                 {typeof category.icon === 'function' ? (
//                   <category.icon className="text-xl" />
//                 ) : (
//                   <FontAwesomeIcon icon={category.icon} />
//                 )}
//                 <span>{category.name}</span>
//               </Link>
//             ))}
//           </div>
//         </div>

//         {/* User Actions */}
//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             <Link
//               to="/wishlist"
//               className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//               onClick={closeSidebar}
//             >
//               <FontAwesomeIcon icon={faHeart} />
//               <span>Wishlist</span>
//               {wishlistCount > 0 && (
//                 <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                   {wishlistCount}
//                 </span>
//               )}
//             </Link> 
//             {isAuthenticated && (
//               <button
//                 className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                 onClick={handleLogout}
//               >
//                 <IoExitOutline className="text-xl" />
//                 <span>Logout</span>
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Become a Seller */}
//         <Link
//           to="/become-seller"
//           className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//           onClick={closeSidebar}
//         >
//           <FontAwesomeIcon icon={faPlus} />
//           <span>Become a Seller</span>
//         </Link>
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               <div className="relative" title="Cart">
//                 <Link to="/cart">
//                   <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                   {cartCount > 0 && (
//                     <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//               </div>

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             <div className="relative" title="Wishlist">
//               <Link to="/wishlist" className="flex items-center space-x-2">
//                 <FontAwesomeIcon icon={faHeart} size="lg" />
//                 {wishlistCount > 0 && (
//                   <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                     {wishlistCount}
//                   </span>
//                 )}
//               </Link>
//             </div>

//             <div className="relative" title="Cart">
//               <Link to="/cart">
//                 <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                 {cartCount > 0 && (
//                   <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                     {cartCount}
//                   </span>
//                 )}
//               </Link>
//             </div>
//             {isAuthenticated ? (
//               <div className="relative" ref={dropdownRef}>
//                 <button 
//                   onClick={toggleUserDropdown}
//                   className="flex items-center space-x-2 focus:outline-none"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                   <span className="text-blue-600 font-medium hidden md:inline">
//                     {user?.name.split(' ')[0] || "Profile"}
//                   </span>
//                   <FontAwesomeIcon 
//                     icon={faChevronDown} 
//                     className={`text-xs transition-transform ${isUserDropdownOpen ? 'transform rotate-180' : ''}`}
//                   />
//                 </button>
                
//                 {isUserDropdownOpen && (
//                   <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                     <Link
//                       to="/profile"
//                       className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                       onClick={() => setIsUserDropdownOpen(false)}
//                     >
//                       Profile
//                     </Link>
//                     <button
//                       onClick={handleLogout}
//                       className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
//                     >
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

// import React, { useState, useEffect, useRef } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import {
//   faHeart,
//   faShoppingCart,
//   faHome,
//   faPlus,
//   faShoppingBasket,
//   faSuitcase,
//   faTruck,
//   faStore,
//   faBars,
//   faUser,
//   faTimes,
//   faChevronDown,
//   faPills,
//   faUtensils,
//   faBed,
//   faTools,
//   faMobileScreen,
//   faShirt,
//   faLaptop,
//   faBasketShopping,
// } from "@fortawesome/free-solid-svg-icons";
// import { MdLocalLaundryService } from "react-icons/md";
// import { IoExitOutline } from "react-icons/io5";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { useSelector, useDispatch } from "react-redux";
// import { logout } from "../../redux/slices/authSlice";
// import { clearWishlist } from "../../redux/slices/wishlistSlice";
// import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
// import Cookies from "js-cookie";
// import SearchBar from "./Searchbar";

// const Header = () => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
//   const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const dropdownRef = useRef(null);

//   const { isAuthenticated, user } = useSelector((state) => state.auth);
//   const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
//   const cartItems = useSelector((state) => state.cart.items);
//   const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

//   // Fetch wishlist when authenticated
//   useEffect(() => {
//     if (isAuthenticated) {
//       dispatch(fetchWishlist());
//     } else {
//       dispatch(clearWishlist());
//     }
//   }, [isAuthenticated, dispatch, location]);

//   // Close dropdown when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setIsUserDropdownOpen(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Prevent body scroll when sidebar is open
//   useEffect(() => {
//     if (isSidebarOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "auto";
//     }
    
//     return () => {
//       document.body.style.overflow = "auto";
//     };
//   }, [isSidebarOpen]);

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   const handleLoginClick = () => {
//     navigate("/login");
//     closeSidebar();
//   };

//   const handleLogout = () => {
//     dispatch(logout());
//     dispatch(clearWishlist());
//     Cookies.remove("ecom_user_token");
//     navigate("/login");
//     closeSidebar();
//     setIsUserDropdownOpen(false);
//   };

//   const closeSidebar = () => {
//     setIsSidebarOpen(false);
//   };

//   const handleSearchSubmit = (query) => {
//     navigate(`/search?query=${encodeURIComponent(query)}`);
//   };

//   const toggleUserDropdown = () => {
//     setIsUserDropdownOpen(!isUserDropdownOpen);
//   };

//   // Desktop Navigation
//   const DesktopNavigation = () => (
//     <div className="bg-gray-100 border-t">
//       <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//         <div className="flex flex-wrap gap-2 md:gap-6">
//           <Link to="/grocery" className={getLinkClass("/grocery")}>
//             <FontAwesomeIcon icon={faBasketShopping} />
//             <span className="ml-2">Grocery</span>
//           </Link>
//           <Link to="/electronic" className={getLinkClass("/electronics")}>
//             <FontAwesomeIcon icon={faLaptop} />
//             <span className="ml-2">Electronics</span>
//           </Link>
//           <Link to="/clothing" className={getLinkClass("/clothing")}>
//             <FontAwesomeIcon icon={faShirt} />
//             <span className="ml-2">Clothing</span>
//           </Link>
//           <Link to="/medicine" className={getLinkClass("/medicine")}>
//             <FontAwesomeIcon icon={faPills} />
//             <span className="ml-2">Medicine</span>
//           </Link>
//           <Link to="/delivery" className={getLinkClass("/delivery")}>
//             <FontAwesomeIcon icon={faUtensils} />
//             <span className="ml-2">Food Delivery</span>
//           </Link>
//           <Link to="/hotel-flight" className={getLinkClass("/hotel-flight")}>
//             <FontAwesomeIcon icon={faBed} />
//             <span className="ml-2">Hotel/Flight</span>
//           </Link>
//           <Link to="/service" className={getLinkClass("/service")}>
//             <FontAwesomeIcon icon={faTools} />
//             <span className="ml-2">Service</span>
//           </Link>
//           <Link to="/digital-showroom" className={getLinkClass("/digital-showroom")}>
//             <FontAwesomeIcon icon={faMobileScreen} />
//             <span className="ml-2">Digital Showroom</span>
//           </Link>
//           <Link to="/laundry" className={getLinkClass("/laundry")}>
//             <MdLocalLaundryService className="text-lg" />
//             <span className="ml-2">Laundry</span>
//           </Link>
//         </div>
        
//         <Link to="/become-seller" className={`${getLinkClass("/become-seller")} bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg`}>
//           <FontAwesomeIcon icon={faPlus} />
//           <span className="ml-2">Become a Seller</span>
//         </Link>
//       </div>
//     </div>
//   );

//   const renderSidebarContent = () => {
//     const navItems = [
//       { path: "/grocery", icon: <FontAwesomeIcon icon={faBasketShopping} />, label: "Grocery" },
//       { path: "/electronic", icon: <FontAwesomeIcon icon={faLaptop} />, label: "Electronics" },
//       { path: "/clothing", icon: <FontAwesomeIcon icon={faShirt} />, label: "Clothing" },
//       { path: "/medicine", icon: <FontAwesomeIcon icon={faPills} />, label: "Medicine" },
//       { path: "/delivery", icon: <FontAwesomeIcon icon={faUtensils} />, label: "Food Delivery" },
//       { path: "/hotel-flight", icon: <FontAwesomeIcon icon={faBed} />, label: "Hotel/Flight" },
//       { path: "/service", icon: <FontAwesomeIcon icon={faTools} />, label: "Service" },
//       { path: "/digital-showroom", icon: <FontAwesomeIcon icon={faMobileScreen} />, label: "Digital Showroom" },
//       { path: "/laundry", icon: <MdLocalLaundryService className="text-xl" />, label: "Laundry" },
//     ];

//     return (
//       <div className="p-6">
//         {/* Close Button */}
//         <div className="flex justify-end mb-6">
//           <button 
//             onClick={closeSidebar}
//             className="text-2xl text-gray-600 hover:text-gray-900"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>
//         </div>

//         {/* User Section */}
//         <div className="mb-8 border-b pb-6">
//           {isAuthenticated ? (
//             <Link 
//               to="/profile" 
//               className="flex items-center space-x-3"
//               onClick={closeSidebar}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">{user?.name || "User"}</p>
//                 <p className="text-gray-600 text-sm">View Profile</p>
//               </div>
//             </Link>
//           ) : (
//             <button
//               className="flex items-center space-x-3 w-full"
//               onClick={handleLoginClick}
//             >
//               <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16" />
//               <div>
//                 <p className="font-semibold text-lg">Login / Signup</p>
//                 <p className="text-gray-600 text-sm">Access account features</p>
//               </div>
//             </button>
//           )}
//         </div>

//         {/* Navigation Links */}
//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Categories</h3>
//           <div className="space-y-2">
//             {navItems.map((item) => (
//               <Link
//                 key={item.path}
//                 to={item.path}
//                 className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 ${
//                   location.pathname === item.path ? "bg-blue-50 text-blue-600" : ""
//                 }`}
//                 onClick={closeSidebar}
//               >
//                 <span>{item.icon}</span>
//                 <span>{item.label}</span>
//               </Link>
//             ))}
//           </div>
//         </div>

//         {/* User Actions */}
//         <div className="mb-8">
//           <h3 className="text-gray-500 text-sm font-medium uppercase mb-3">Your Account</h3>
//           <div className="space-y-2">    
//             <Link
//               to="/wishlist"
//               className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100"
//               onClick={closeSidebar}
//             >
//               <FontAwesomeIcon icon={faHeart} />
//               <span>Wishlist</span>
//               {wishlistCount > 0 && (
//                 <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
//                   {wishlistCount}
//                 </span>
//               )}
//             </Link> 
//             {isAuthenticated && (
//               <button
//                 className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 w-full text-left text-red-600"
//                 onClick={handleLogout}
//               >
//                 <IoExitOutline className="text-xl" />
//                 <span>Logout</span>
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Become a Seller */}
//         <Link
//           to="/become-seller"
//           className="flex items-center space-x-3 p-3 bg-blue-50 text-blue-600 rounded-lg font-medium"
//           onClick={closeSidebar}
//         >
//           <FontAwesomeIcon icon={faPlus} />
//           <span>Become a Seller</span>
//         </Link>
//       </div>
//     );
//   };

//   return (
//     <div className="bg-white shadow-md">
//       {/* Mobile Top Bar */}
//       <div className="lg:hidden">
//         <div className="container mx-auto">
//           <div className="flex justify-between items-center p-4">
//             <div className="flex items-center cursor-pointer" onClick={()=>{navigate("/")}}>
//               <img src="/assets/logo.png" alt="Logo" className="h-8 cursor-pointer" />
//             </div>
            
//             <div className="flex items-center space-x-4">
//               <div className="relative" title="Cart">
//                 <Link to="/cart">
//                   <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                   {cartCount > 0 && (
//                     <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                       {cartCount}
//                     </span>
//                   )}
//                 </Link>
//               </div>

//               <button
//                 onClick={() => setIsSidebarOpen(true)}
//                 className="text-2xl"
//               >
//                 <FontAwesomeIcon icon={faBars} />
//               </button>
//             </div>
//           </div>

//           {/* Mobile Search Bar */}
//           <SearchBar isMobile={true} onSearchSubmit={handleSearchSubmit} />
//         </div>
//       </div>

//       {/* Desktop Top Bar */}
//       <div className="hidden lg:block">
//         <div className="container mx-auto flex flex-col md:flex-row justify-between items-center p-4">
//           {/* Logo */}
//           <div className="flex items-center space-x-4 mb-4 md:mb-0 cursor-pointer" onClick={()=>{navigate("/")}}>
//             <img src="/assets/logo.png" alt="Logo" className="h-10" />
//           </div>

//           {/* Desktop Search Bar */}
//           <SearchBar onSearchSubmit={handleSearchSubmit} />

//           {/* Right Side Icons */}
//           <div className="flex items-center space-x-4 md:space-x-6 w-full md:w-auto justify-center">
//             <div className="relative" title="Wishlist">
//               <Link to="/wishlist" className="flex items-center space-x-2">
//                 <FontAwesomeIcon icon={faHeart} size="lg" />
//                 {wishlistCount > 0 && (
//                   <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                     {wishlistCount}
//                   </span>
//                 )}
//               </Link>
//             </div>

//             <div className="relative" title="Cart">
//               <Link to="/cart">
//                 <FontAwesomeIcon icon={faShoppingCart} size="lg" />
//                 {cartCount > 0 && (
//                   <span className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-red-600 rounded-full">
//                     {cartCount}
//                   </span>
//                 )}
//               </Link>
//             </div>
//             {isAuthenticated ? (
//               <div className="relative" ref={dropdownRef}>
//                 <button 
//                   onClick={toggleUserDropdown}
//                   className="flex items-center space-x-2 focus:outline-none"
//                 >
//                   <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                   <span className="text-blue-600 font-medium hidden md:inline">
//                     {user?.name.split(' ')[0] || "Profile"}
//                   </span>
//                   <FontAwesomeIcon 
//                     icon={faChevronDown} 
//                     className={`text-xs transition-transform ${isUserDropdownOpen ? 'transform rotate-180' : ''}`}
//                   />
//                 </button>
                
//                 {isUserDropdownOpen && (
//                   <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                     <Link
//                       to="/profile"
//                       className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                       onClick={() => setIsUserDropdownOpen(false)}
//                     >
//                       Profile
//                     </Link>
//                     <button
//                       onClick={handleLogout}
//                       className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
//                     >
//                       Logout
//                     </button>
//                   </div>
//                 )}
//               </div>
//             ) : (
//               <div
//                 className="flex items-center space-x-2 cursor-pointer"
//                 onClick={handleLoginClick}
//               >
//                 <FontAwesomeIcon icon={faUser} size="lg" className="text-blue-600" />
//                 <button className="text-blue-600 font-medium hidden md:inline">Login</button>
//               </div>
//             )}

            
//           </div>
//         </div>
//         <DesktopNavigation />
//       </div>

//       {/* Mobile Sidebar */}
//       {isSidebarOpen && (
//         <div className="fixed inset-0 z-50 lg:hidden">
//           {/* Overlay */}
//           <div 
//             className="fixed inset-0 bg-black bg-opacity-50"
//             onClick={closeSidebar}
//           ></div>
          
//           {/* Sidebar Content */}
//           <div className="fixed inset-y-0 right-0 w-4/5 max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
//             {renderSidebarContent()}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Header;

